/**
 * Chat widget view for the login pane/screen.
 */
import springyView from '../../tools/SpringyView.js';
import { focusFirst } from '../../tools/focusUtil.js';

const loginView = Object.create(springyView);

/**
 * @prop {patronSession} patronSession Data about patron.
 */
loginView.patronSession = null;
/**
 * @prop {Object} $form Jquery form object.
 */
loginView.$form = null;

/**
 * @prop {Object} events Default events.
 */
loginView.events = {
    'submit form': 'processLoginForm',
    'click .s-lch-btn-close': 'handleCancelClick'
};
/**
 * @prop {Object} listeners
 */
loginView.listeners = {
    chatError: 'showLoginError',
    chatBlockedError: 'showLoginError'
};
/**
 * Wrapper for springyView.init in case we need extra functionality.
 * @param {Object} config Config data.
 * @param {chatWidget} config.model Widget object for this view.
 * @param {Object} config.$el Div for this view. $('#login') for now.
 * @param {patronSession} patronSession Patron session object.
 */
loginView.initialize = function(config) {
    if (config.patronSession) {
        this.patronSession = config.patronSession;
    }
    config.$el = window.$('#login');
    this.init(config);
    if (!this.model.isEmbed()) {
        this.addEvent('keydown', 'trapFocus');
    }
};
/**
 * Validate name field.
 * @return {Boolean}
 */
loginView.validateName = function() {
    const $nameInput = this.$form.find('input[name=client_name]');
    let client_name = $nameInput.val().trim();
    // Check name is blank
    if (this.model.get('autologin') === true) {
        client_name = this.model.generateAnonName();
    } else if (client_name === '' && !this.model.get('autologin')) {
        if (this.model.get('enable_anon')) {
            client_name = this.model.generateAnonName();
            $nameInput.val(client_name);
        } else {
            this.showValidationError($nameInput, this.model.get('valid_form_n'));
            $nameInput.focus();
            return false;
        }
    }
    this.patronSession.set('name', client_name);
    return true;
};
/**
 * Validate contact info.
 * @return {Boolean}
 */
loginView.validateContact = function() {
    if (this.model.get('contact_request')) {
        const $contactInput = this.$form.find('input[name=client_contact]');
        const contact = $contactInput.val().trim();
        // validate
        if (!this.model.validateContact(contact)) {
            this.showValidationError($contactInput, this.model.get('valid_form_c'));
            $contactInput.focus();
            return false;
        }
        // This will reformat and set.
        this.patronSession.setContactInfo(contact);
    }
    return true;
};
/**
 * Validate question.
 * @return {Boolean}
 */
loginView.validateQuestion = function() {
    const $questionInput = this.$form.find('textarea[name=client_question]');
    const client_question = ($questionInput.length === 1) ? $questionInput.val().trim() : '';

    if (this.model.get('initial_question') && !this.model.get('autologin')) {
        if (client_question === '') {
            this.showValidationError($questionInput, this.model.get('valid_form_q'));
            $questionInput.focus();
            return false;
        }
    }
    this.patronSession.set('client_question', this.model.sanitizeMessage(client_question));
    this.model.set('client_question', this.patronSession.get('client_question'));
    return true;
};
/**
 * Parse/validate custom fields.
 * NOTE: code repeated in chatWidgetChatView.contactSave we should probably centralize it somewhere else.
 * @returns {Boolean}
 */
loginView.validateCustomFields = function() {
    const customFields = this.model.get('customFields');
    if (!Array.isArray(customFields) || customFields.length === 0) {
        return true;
    }
    let isValid = true;
    customFields.forEach((field) => {
        if (!field.show) {
            return;
        }
        const fieldInput = this.$form.find(`#s-lch-user${field.id}`);
        if (!fieldInput || fieldInput.length === 0) {
            return;
        }
        const value = fieldInput.val().trim();
        if (field.required) {
            let invalid = false;
            if (field.type === 'l'
                && (!fieldInput[0].selectedIndex || fieldInput[0].selectedIndex <= 0)) {
                // is it unselected or the first empty/0 option.
                invalid = true;
            } else if (value === '') {
                invalid = true;
            }
            if (invalid) {
                isValid = false;
                this.showValidationError(fieldInput, this.model.get('translation').required || 'Required');
                fieldInput.focus();
                return;
            }
        }
        this.patronSession.set(`user${field.id}`, {
            name: field.name,
            val: fieldInput.val().trim()
        });
    });
    return isValid;
};
/**
 * Process/parse/validate the login form.
 * Return false if validation fails.
 * @param {Object} e Submit event
 * @return {Boolean}
 */
loginView.processLoginForm = function(e) {
    e.preventDefault();
    this.$submitButton.prop('disabled', true);
    this.$form.find('.s-lch-form-error').remove();

    if (!this.validateName()
        || !this.validateContact()
        || !this.validateQuestion()
        || !this.validateCustomFields()) {
        this.$submitButton.prop('disabled', false);
        return false;
    }

    // Check what op or dept is set in the form so we know who to connect to
    // Check op first, then dept.
    const $opidField = this.$form.find('*[name=opId]');
    const $deptidField = this.$form.find('*[name=dept]');
    if ($opidField.length > 0) {
        this.patronSession.set('uid', Number($opidField.val()));
    }
    if (this.patronSession.get('uid') < 1 && $deptidField.length > 0) {
        this.patronSession.set('did', Number($deptidField.val()));
    }
    // this will trigger the start session on the controller.
    this.model.set('loggedIn', true);
    return true;
};
/**
 * Show login/session start error.
 * @param {String} error Optional error text
 * @param {Boolean} showContact Should we display the contact form (unused in this view).
 */
loginView.showLoginError = function(error = '') {
    const msg = (!error) ? this.model.getErrorMessage() : error;
    this.$form.replaceWith(`<p class="alert alert-danger">${msg}</p>`);
};
/**
 * Handler: Cancel button (for feedback widget embeds only)
 */
loginView.handleCancelClick = function() {
    this.model.emitter.trigger('closeWidget');
};
/**
 * Show a validation error.
 * @param {Object} $field jquery object of field that error is on.
 * @param {String} msg Message to show.
 */
loginView.showValidationError = function($field, msg) {
    if (msg === '') {
        return;
    }
    $field.prev('label').append(`<span class="text-danger s-lch-form-error">${msg}</span>`);
};
/**
 * Show this view and set focus as appopriate
 */
loginView.showView = function() {
    this.$el.removeClass('hidden');
    if (this.model.get('auth_id') > 0 && !this.model.get('isAuthValidated')) {
        // When LibAuth overlay is shown we want to make this view hidden for screenreaders
        // And disable any form elements from keyboard interaction.
        // Disable inputs to stop focus
        this.$el[0].querySelectorAll('input,select,button,textarea').forEach((el) => {
            el.disabled = true;
        });
        // hide form for screenreaders
        this.$el[0].setAttribute('aria-hidden', 'true');
        return;
    }
    if (this.model.isEmbed()) {
        return;
    }
    focusFirst(this.el);
};
/**
 * Repopulate the login form after re-render.
 * @param {FormData} formData Form data from previous render
 */
loginView.repopulateForm = function(formData) {
    const fields = [
        'client_name',
        'client_contact',
        'client_question',
        'dept',
        'user1',
        'user2',
        'user3'
    ];
    const form = this.$form[0];
    Array.from(formData.entries()).forEach(([key, value]) => {
        if (fields.indexOf(key) < 0) {
            return;
        }
        const input = form.elements[key];
        if (!input) {
            return;
        }
        // All input, textarea, or select at this point.
        switch (input.tagName) {
            case 'SELECT':
                // Make sure the value is still there as an option.
                // Don't query by value cause it could have chars that would need escaping
                input.querySelectorAll('option').forEach((opt) => {
                    if (opt.value === value) {
                        input.value = value;
                    }
                });
                break;
            default:
                input.value = value;
                break;
        }
    });
};
/**
 * Are we in an iframe (helpful for close btn)
 * @return {Boolean}
 */
loginView.inIframe = function() {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};
/**
 * Render the view
 * @return {autoView}
 */
loginView.render = function() {
    // Grab form values if already filled.
    let formData = null;
    if (this.$form !== null) {
        formData = new FormData(this.$form[0]);
    }

    // clear out existing content, events, etc.
    this.$el.empty();

    // render the template and put it in the dom.
    const templateData = this.model.toJSON();
    templateData.patron = this.patronSession.toJSON();
    templateData.inIframe = this.inIframe();
    this.$el.html(this.templateEng.render('pane_login.njk', templateData));

    this.$form = this.$el.find('#s-lch-login-form');
    this.$submitButton = this.$form.find('button[type=submit]');

    if (formData !== null) {
        this.repopulateForm(formData);
    }

    return this;
};

export default loginView;
